import { Box, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import arrowRightIcon from "/assets/arrow-right.svg";
import { Button } from "./Button";
import { useAppUrl } from "../hooks/useAppUrl";

const InfoSectionContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
});

const Frame = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "60px 0",
  gap: "60px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    gap: "20px",
    alignItems: "flex-start",
    padding: "40px 0",
  },
}));

const FrameReversed = styled(Frame)({
  flexDirection: "row-reverse",
});

const ContentSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "600px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const TextContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  [theme.breakpoints.down("md")]: {
    fontSize: "var(--heading-h2-mobile-font-size)",
    lineHeight: "var(--heading-h2-mobile-line-height)",
  },
}));

const SectionDescription = styled(Typography)({
  color: "var(--text-secondary)",
});

const ShareLink = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.8,
  },
});

const ShareLinkText = styled(Typography)({
  color: "var(--link-default)",
  fontSize: "var(--paragraph-p3-font-size)",
  fontWeight: "var(--paragraph-p3-medium-font-weight)",
  lineHeight: "var(--paragraph-p3-line-height)",
  letterSpacing: "var(--paragraph-p3-letter-spacing)",
  fontFamily: "var(--font-family)",
  whiteSpace: "nowrap",
});

const ArrowIcon = styled(Box)({
  width: "16px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: "100%",
    height: "100%",
    display: "block",
  },
});

const ImageSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "484px",
  height: "424px",
  backgroundColor: "var(--background-tertiary)",
  borderRadius: "32px",
  overflow: "hidden",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "336px",
    height: "294px",
    borderRadius: "24px",
  },
}));

const SectionImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
});

const StepsList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const StepItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px",
  border: "1px solid var(--button-border)",
  borderRadius: "12px",
});

const stepTextStyles = {
  color: "var(--text-secondary)",
  fontSize: "var(--paragraph-p2-font-size)",
  fontWeight: "var(--heading-h4-semibold-font-weight)",
  lineHeight: "var(--paragraph-p2-line-height)",
  fontFamily: "var(--font-family)",
};

const StepNumber = styled("ol")({
  ...stepTextStyles,
  margin: 0,
  paddingLeft: "27px",
  listStyle: "decimal",
});

const StepTitle = styled("span")(stepTextStyles);

const StepDescription = styled(Typography)({
  color: "var(--text-secondary)",
});

interface Step {
  number: number;
  titleKey: string;
  descriptionKey: string;
}

const stepKeys: Step[] = [
  { number: 1, titleKey: "info.step1Title", descriptionKey: "info.step1Description" },
  { number: 2, titleKey: "info.step2Title", descriptionKey: "info.step2Description" },
  { number: 3, titleKey: "info.step3Title", descriptionKey: "info.step3Description" },
];

export const InfoSection = () => {
  const { t } = useTranslation();
  const appUrl = useAppUrl();

  return (
    <InfoSectionContainer>
      <Frame>
        <ContentSection>
          <TextContent>
            <SectionTitle variant="h2">{t("info.shareTitle")}</SectionTitle>
            <SectionDescription variant="body2">
              {t("info.shareDescription")}
            </SectionDescription>
          </TextContent>
          <ShareLink>
            <ShareLinkText>{t("info.shareLink")}</ShareLinkText>
            <ArrowIcon>
              <img src={arrowRightIcon} alt="" />
            </ArrowIcon>
          </ShareLink>
        </ContentSection>
        <ImageSection>
          <SectionImage src="/assets/social.webp" alt={t("images.shareAlt")} />
        </ImageSection>
      </Frame>

      <FrameReversed>
        <ContentSection>
          <TextContent>
            <SectionTitle variant="h2">{t("info.growthTitle")}</SectionTitle>
            <SectionDescription variant="body2">
              {t("info.growthDescription")}
            </SectionDescription>
          </TextContent>
        </ContentSection>
        <ImageSection>
          <SectionImage src="/assets/my-skills.webp" alt={t("images.mySkillsAlt")} />
        </ImageSection>
      </FrameReversed>

      <Frame>
        <ContentSection>
          <TextContent>
            <SectionTitle variant="h2">{t("info.howItWorksTitle")}</SectionTitle>
            <StepsList>
              {stepKeys.map((step) => (
                <StepItem key={step.number}>
                  <StepNumber start={step.number}>
                    <li>
                      <StepTitle>{t(step.titleKey)}</StepTitle>
                    </li>
                  </StepNumber>
                  <StepDescription variant="body2">{t(step.descriptionKey)}</StepDescription>
                </StepItem>
              ))}
            </StepsList>
          </TextContent>
        </ContentSection>
        <ImageSection>
          <SectionImage src="/assets/how-it-works.webp" alt={t("images.howItWorksAlt")} />
        </ImageSection>
      </Frame>

      <Button 
        variant="primary" 
        size="m"
        onClick={() => window.open(appUrl, '_blank', 'noopener,noreferrer')}
      >
        {t("info.cta")}
      </Button>
    </InfoSectionContainer>
  );
};
